import {
  Alert,
  Box,
  Collapse,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { format, parseISO } from "date-fns";
import { useContext, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../../app/hooks";
import FormGridRow from "../../../../../common/components/functional/formGridRow/FormGridRow";
import { SimpleTable } from "../../../../../common/components/functional/simpleTable/SimpleTable";
import AddButton from "../../../../../common/components/functional/addButton/AddButton";
import { CustomButton } from "../../../../../common/components/functional/customButton/CustomButton";
import { useConfirmationDialog } from "../../../../../contexts/ConfirmationDialogContext";
import type { Employment } from "../../../../employment/employmentApi";
import {
  fetchEmploymentById,
  removePeriodStartSetting,
} from "../../../../employment/employmentSlice";
import {
  employmentDateInHelsinki,
  periodStartBaseline,
  resolvePeriodStartDate,
} from "../../../../employment/periodStartSettings";
import { AccessRightContext } from "../../../../user/AccessRightProvider";
import {
  PeriodStartDraft,
  PeriodStartSettingsEditor,
} from "./PeriodStartSettingsEditor";
import { PeriodStartRow, periodStartRows } from "./helpers";
import { PeriodStartActionButton, PeriodStartActions } from "./styles";

import { UpcomingPeriodStartChanges } from "./UpcomingPeriodStartChanges";

export const PeriodStartSettings = ({
  employment,
}: {
  employment: Employment;
}) => {
  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();
  const { confirm } = useConfirmationDialog();
  const { canEditEmployment } = useContext(AccessRightContext);
  const canEdit = canEditEmployment(employment.employerId);
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const [draft, setDraft] = useState<PeriodStartDraft | null>(null);
  const [removing, setRemoving] = useState(false);
  const [failure, setFailure] = useState<"conflict" | "failed" | null>(null);
  const [date, setDate] = useState(employmentDateInHelsinki);
  useEffect(() => {
    const interval = window.setInterval(
      () => setDate(employmentDateInHelsinki()),
      60_000,
    );
    return () => window.clearInterval(interval);
  }, []);
  const settings = employment.details.periodStartSettings ?? [];
  const upcoming = settings
    .filter((setting) => setting.from > date)
    .sort((a, b) => a.from.localeCompare(b.from));
  const baseline = periodStartBaseline(employment.details);
  const current = resolvePeriodStartDate(baseline, settings, date);
  const rows = periodStartRows(employment.details);
  // Keep an open edit mounted even if a background projection read removes its row.
  const visibleRows =
    draft?.row &&
    !rows.some((row) => row.setting.from === draft.row!.setting.from)
      ? [...rows, draft.row].sort((a, b) =>
          a.setting.from.localeCompare(b.setting.from),
        )
      : rows;
  const activeRow = rows.filter((row) => row.setting.from <= date).at(-1);
  const displayDate = (value: string | null) =>
    value ? format(parseISO(value), "dd.MM.yyyy") : "-";
  const close = () => {
    setDraft(null);
    setFailure(null);
  };
  const onError = (code?: string) =>
    setFailure(code === "409" ? "conflict" : "failed");
  const startEdit = (row?: PeriodStartRow) => {
    setFailure(null);
    setDraft({ employment, row });
  };
  const remove = async () => {
    if (!canEdit || removing || !draft?.row || draft.row.isBaseline) return;
    const { employment: openingEmployment, row } = draft;
    setRemoving(true);
    setFailure(null);
    const restored = resolvePeriodStartDate(
      periodStartBaseline(openingEmployment.details),
      (openingEmployment.details.periodStartSettings ?? []).filter(
        (setting) => setting.from !== row.setting.from,
      ),
      row.setting.from,
    );
    const confirmed = await confirm({
      title: t("common:confirmDialogDelete"),
      contentText: t("periodStartRemoveConfirmation", {
        from: displayDate(row.setting.from),
        periodStart: displayDate(restored),
      }),
      confirmText: t("common:delete"),
      cancelText: t("common:cancel"),
      ConfirmButtonProps: { color: "error" },
    });
    if (confirmed) {
      const result = await dispatch(
        removePeriodStartSetting({
          employmentId: openingEmployment.employmentId,
          version: openingEmployment.details.version,
          from: row.setting.from,
        }),
      );
      if (removePeriodStartSetting.fulfilled.match(result)) close();
      else onError(result.error.code);
    }
    setRemoving(false);
  };

  return (
    <FormGridRow
      label={t("periodStarts")}
      labelSize={2}
      fieldSize={10}
      multiRow
      GridLabelProps={{ sx: { mt: 0 } }}
    >
      <Box sx={{ maxWidth: 620 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.5,
            minHeight: 24,
          }}
        >
          <Typography component="span">{displayDate(current)}</Typography>
          {activeRow &&
            !activeRow.isBaseline &&
            activeRow.setting.from !== employment.details.agreedStart && (
              <Typography component="span" color="text.secondary">
                (
                {t("periodStartSince", {
                  date: displayDate(activeRow.setting.from),
                })}
                )
              </Typography>
            )}
          {upcoming.length > 0 && (
            <UpcomingPeriodStartChanges settings={upcoming} />
          )}
          <PeriodStartActionButton
            aria-label={t(
              expanded ? "periodStartHideChanges" : "periodStartShowChanges",
            )}
            aria-expanded={expanded}
            aria-controls={detailsId}
            onClick={() => setExpanded(!expanded)}
            disabled={!!draft || removing}
            sx={{ my: "-8px" }}
          >
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </PeriodStartActionButton>
        </Box>
        <Collapse in={expanded} timeout={100} unmountOnExit id={detailsId}>
          <Box sx={{ pt: 1 }}>
            <SimpleTable
              tableSx={{
                tableLayout: "fixed",
                "& th, & td": { px: 1, py: 1.25, verticalAlign: "top" },
                "& th:last-child, & td:last-child": { width: 152 },
              }}
              header={
                <TableRow>
                  <TableCell>{t("periodStartEffectiveFrom")}</TableCell>
                  <TableCell>{t("periodStarts")}</TableCell>
                  <TableCell />
                </TableRow>
              }
              items={[
                ...visibleRows.map((row) =>
                  draft?.row?.setting.from === row.setting.from ? (
                    <PeriodStartSettingsEditor
                      key={`edit-${row.setting.from}`}
                      draft={draft}
                      canEdit={canEdit}
                      onClose={close}
                      onError={onError}
                      onDelete={draft.row?.isBaseline ? undefined : remove}
                      isDeleting={removing}
                    />
                  ) : (
                    <TableRow key={row.setting.from}>
                      <TableCell>
                        <Typography variant="body2">
                          {displayDate(row.setting.from)}
                        </Typography>
                        {row.setting.from === activeRow?.setting.from && (
                          <Typography variant="caption" color="text.secondary">
                            {t("periodStartCurrent")}
                          </Typography>
                        )}
                        {row.setting.from > date && (
                          <Typography variant="caption" color="text.secondary">
                            {t("periodStartUpcoming")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {displayDate(row.setting.periodStart)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {canEdit && (
                          <PeriodStartActions sx={{ my: "-10px" }}>
                            <PeriodStartActionButton
                              aria-label={`${t("common:edit")} ${displayDate(row.setting.from)}`}
                              disabled={!!draft || removing}
                              onClick={() => startEdit(row)}
                            >
                              <EditOutlinedIcon />
                            </PeriodStartActionButton>
                          </PeriodStartActions>
                        )}
                      </TableCell>
                    </TableRow>
                  ),
                ),
                ...(draft && !draft.row
                  ? [
                      <PeriodStartSettingsEditor
                        key="add"
                        draft={draft}
                        canEdit={canEdit}
                        onClose={close}
                        onError={onError}
                      />,
                    ]
                  : []),
              ]}
            />
            {canEdit && (
              <AddButton
                label={t("common:add")}
                onClick={() => startEdit()}
                disabled={!!draft || removing}
                sx={{ mt: 1.5 }}
              />
            )}
            {failure && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {t(
                  failure === "conflict"
                    ? "periodStartSettingsConflict"
                    : "periodStartSettingsSaveFailed",
                )}
                {failure === "conflict" && (
                  <CustomButton
                    variant="text"
                    disabled={removing}
                    onClick={() => {
                      close();
                      dispatch(fetchEmploymentById(employment.employmentId));
                    }}
                  >
                    {t("periodStartDiscardAndReload")}
                  </CustomButton>
                )}
              </Alert>
            )}
          </Box>
        </Collapse>
      </Box>
    </FormGridRow>
  );
};
